import { useCallback, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import {
	type UseFetchConfigAnalytics,
	useThirdPartyConfig,
} from '@atlassian/rovo-connectors-config/hooks';
import {
	update3pConfigCacheEntry,
	type TSearch3pConfigAPIResponse,
} from '@atlassian/search-client/fetch-search-config';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	EXTERNAL_PRODUCT_CONNECTION,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from '../__types__/RemoteObject.ts';

export type ThirdPartyAuthenticationResult = {
	authenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	isLoading: boolean;
	refetch: () => Promise<void>;
	markOptimisticallyAuthenticated: (product: ExternalProductConnection) => void;
};

const SPACE_CURATION_3P_AUTH = 'spaceCurationThirdPartyAuth';

const CONNECTION_PRODUCT_KEYS: Partial<Record<ExternalProductConnection, ProductKeys3P[]>> = {
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: [ProductKeys3P.GoogleDrive],
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT]: [ProductKeys3P.OneDrive, ProductKeys3P.Sharepoint],
};

const CONNECTION_RESOURCE_TYPES: Partial<Record<ExternalProductConnection, string[]>> = {
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: ['google-drive-connector'],
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT]: ['onedrive-connector', 'sharepoint-connector'],
};

type UseFetch3PAuthenticationsProps = {
	products: ExternalProductConnection[];
	cloudId: string;
};

export const useFetch3PAuthentications = ({
	products,
	cloudId,
}: UseFetch3PAuthenticationsProps): ThirdPartyAuthenticationResult => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [optimisticallyAuthenticatedProducts, setOptimisticallyAuthenticatedProducts] = useState<
		Set<ExternalProductConnection>
	>(new Set());

	const fireAnalyticsEvent: UseFetchConfigAnalytics['fireAnalyticsEvent'] = useCallback(
		(event) => createAnalyticsEvent(event).fire(),
		[createAnalyticsEvent],
	);

	const logException: UseFetchConfigAnalytics['logException'] = useCallback((exception) => {
		getMonitoringClient().submitError(exception, {
			attribution: Attribution.INTEGRATIONS,
			partialFailure: true,
		});
	}, []);

	const analytics: UseFetchConfigAnalytics = useMemo(
		() => ({
			searchSessionId: SPACE_CURATION_3P_AUTH,
			source: SPACE_CURATION_3P_AUTH,
			actionSubject: SPACE_CURATION_3P_AUTH,
			taskNamePrefix: SPACE_CURATION_3P_AUTH,
			fireAnalyticsEvent,
			logException,
		}),
		[fireAnalyticsEvent, logException],
	);

	const disabled = !cloudId || !userId || products.length === 0;
	const { thirdPartyConfigs } = useThirdPartyConfig({
		cloudId,
		userId: userId ?? undefined,
		disabled,
		analytics,
	});

	const markOptimisticallyAuthenticated = useCallback(
		(product: ExternalProductConnection) => {
			setOptimisticallyAuthenticatedProducts((prev) => {
				const next = new Set(prev);
				next.add(product);
				return next;
			});

			const resourceTypes = CONNECTION_RESOURCE_TYPES[product];
			if (userId && cloudId && resourceTypes) {
				const resourceTypeSet = new Set(resourceTypes);
				void update3pConfigCacheEntry(userId, cloudId, (config: TSearch3pConfigAPIResponse) =>
					config.map((resource) =>
						resourceTypeSet.has(resource.type) ? { ...resource, isUserOAuthed: true } : resource,
					),
				);
			}
		},
		[cloudId, userId],
	);

	const { authenticatedServices, unauthenticatedServices } = useMemo(() => {
		const authenticated: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};
		const unauthenticated: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};

		for (const product of products) {
			const productKeys = CONNECTION_PRODUCT_KEYS[product];
			if (!productKeys) {
				continue;
			}

			let hasConfig = false;
			let isAuthenticated = false;
			let outboundAuthUrl: string | undefined;
			let icon: AuthenticationStatus['icon'];

			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
			for (const productKey of productKeys) {
				const config = thirdPartyConfigs?.[productKey];
				if (!config) {
					continue;
				}
				if (!hasConfig) {
					icon = config.icon;
					hasConfig = true;
				}
				if (!config.userNeedsOAuth) {
					isAuthenticated = true;
				}
				if (outboundAuthUrl === undefined && config.outboundAuthUrl) {
					outboundAuthUrl = config.outboundAuthUrl;
				}
			}

			if (!hasConfig) {
				continue;
			}

			const status: AuthenticationStatus = { isAuthenticated, outboundAuthUrl, icon };

			if (isAuthenticated) {
				authenticated[product] = status;
			} else if (!optimisticallyAuthenticatedProducts.has(product)) {
				unauthenticated[product] = status;
			}
		}

		return { authenticatedServices: authenticated, unauthenticatedServices: unauthenticated };
	}, [products, thirdPartyConfigs, optimisticallyAuthenticatedProducts]);

	const refetch = useCallback(async () => {}, []);

	const isLoading = !disabled && thirdPartyConfigs === undefined;

	return {
		authenticatedServices,
		unauthenticatedServices,
		isLoading,
		refetch,
		markOptimisticallyAuthenticated,
	};
};
